import { Locale, formatDistanceToNow, intervalToDuration } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import i18n from 'i18n';
import { LanguageService } from 'services/language-service';
import { SECONDS_PER_MINUTE } from 'shared/constants/general-constants';
import { LANGUAGES } from 'shared/constants/languages';

export const getRelativeReadableTimeString = (timestamp: number) => {
  if (!timestamp) return '';

  const language = LanguageService.getCurrentLanguage();

  let locale: Locale;
  if (language === LANGUAGES.english) {
    locale = enUS;
  } else if (language === LANGUAGES.french) {
    locale = fr;
  } else {
    locale = enUS;
  }

  return formatDistanceToNow(new Date(timestamp), {
    addSuffix: true,
    locale
  });
};

export const formatSecondsToFriendlyDuration = (durationSeconds: number) => {
  const duration = intervalToDuration({ start: 0, end: durationSeconds * 1000 });
  const parts = [];
  if (duration.days) parts.push(`${duration.days}d`);
  if (duration.hours) parts.push(`${duration.hours}h`);
  if (duration.minutes) parts.push(`${duration.minutes}m`);
  if (duration.seconds || (!parts.length && !duration.seconds)) {
    parts.push(`${duration.seconds || 0}s`);
  }
  return parts.join(' ');
};

export const formatSecondsToTranscriptTimestamp = (secondsFromStart: number): string => {
  const minutes = Math.floor(secondsFromStart / 60);
  const seconds = Math.floor(secondsFromStart % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const formatDate = (
  timestamp: number,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
) => {
  const date = new Date(timestamp);
  const language = LanguageService.getCurrentLanguage();
  return date.toLocaleDateString(language === LANGUAGES.english ? 'en-us' : 'fr-fr', options);
};

export const formatNumberWithSpacesLabel = (num: number, max: number): string => {
  if (max === 0) return `${i18n.t('USAGE.NOT_AVAILABLE')}`;
  return `${formatNumberWithSpaces(Math.min(num, max))} / ${formatNumberWithSpaces(max)}`;
};

export const formatFileSizeLabel = (num: number, max: number) => {
  if (max === 0) return `${i18n.t('USAGE.NOT_AVAILABLE')}`;
  return `${formatFileSize(Math.min(num, max))} / ${formatFileSize(max)}`;
};

export const formatMinutesLabelFromSeconds = (numSeconds: number, maxSeconds: number): string => {
  if (maxSeconds === 0) return `${i18n.t('USAGE.NOT_AVAILABLE')}`;
  const value = Math.min(numSeconds / SECONDS_PER_MINUTE, maxSeconds / SECONDS_PER_MINUTE);
  if (value === maxSeconds / SECONDS_PER_MINUTE) return `${value} / ${value}`;
  return `${Math.min(numSeconds / SECONDS_PER_MINUTE, maxSeconds / SECONDS_PER_MINUTE).toFixed(2)} / ${
    maxSeconds / SECONDS_PER_MINUTE
  }`;
};

export const formatNumberWithSpaces = (num: number): string => {
  const numStr = num.toString().split('');
  const formattedNum = [];
  let digitsProcessed = 0;

  for (let i = numStr.length - 1; i >= 0; i--) {
    formattedNum.unshift(numStr[i]);
    digitsProcessed++;

    if (digitsProcessed % 3 === 0 && i !== 0) {
      formattedNum.unshift(' ');
    }
  }

  return formattedNum.join('');
};

export const formatHexColor = (hexColor?: string) => {
  if (hexColor?.trim().length === 0) return '';
  if (!hexColor) return undefined;
  if (hexColor.startsWith('#')) {
    return hexColor.toUpperCase();
  }
  return `#${hexColor}`.toUpperCase();
};

export const formatFileSize = (bytes: number, decimals = 2) => {
  if (bytes === 0) {
    return '0';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const formatInitials = (firstName?: string, lastName?: string) =>
  !firstName || !lastName || isDefaultName(firstName) || isDefaultName(lastName)
    ? ''
    : `${firstName.at(0)?.toUpperCase()}${lastName.at(0)?.toUpperCase()}`;

export const isDefaultName = (name: string) => {
  const defaultNames = [DEFAULT_LN, DEFAULT_FN];
  return defaultNames.includes(name);
};

export const formatName = (firstName?: string, lastName?: string) => {
  let value = `${isDefaultName(firstName ?? DEFAULT_FN) ? '-' : firstName}`;
  if (!lastName) return value;
  value += ` ${isDefaultName(lastName ?? DEFAULT_LN) ? '-' : lastName}`;
  return value;
};

export const DEFAULT_LN = 'DEFAULT_LN';
export const DEFAULT_FN = 'DEFAULT_FN';

export const dedent = (str: string): string => {
  const lines = str.split('\n');

  // Find the minimum indentation of non-empty lines
  const nonEmptyLines = lines.filter((line) => line.trim() !== '');
  if (nonEmptyLines.length === 0) return '';

  const minIndent = Math.min(
    ...nonEmptyLines.map((line) => {
      const match = line.match(/^\s*/);
      return match ? match[0].length : 0;
    })
  );

  // Remove the common indentation from all lines
  const dedentedLines = lines.map((line) => {
    if (line.trim() === '') return line; // Preserve empty lines as-is
    return line.slice(minIndent);
  });

  return dedentedLines.join('\n').trim();
};
